import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePlatformConfig } from "../lib/platformConfig";
import { toast } from "sonner";

const RECAPTCHA_SCRIPT_ID = "kc-recaptcha-script";

/** Inject the reCAPTCHA v2 script once. Resolves when `window.grecaptcha`
 * is ready. Idempotent — multiple calls reuse the same promise so React
 * StrictMode double-invocations don't load it twice. */
function loadRecaptchaScript() {
    if (typeof window === "undefined") return Promise.resolve(null);
    if (window.grecaptcha && window.grecaptcha.render) {
        return Promise.resolve(window.grecaptcha);
    }
    if (window.__kcRecaptchaPromise) return window.__kcRecaptchaPromise;
    window.__kcRecaptchaPromise = new Promise((resolve, reject) => {
        // Google calls this when ready
        window.__kcRecaptchaOnload = () => {
            resolve(window.grecaptcha);
        };
        const existing = document.getElementById(RECAPTCHA_SCRIPT_ID);
        if (existing) {
            existing.addEventListener("load", () => resolve(window.grecaptcha));
            existing.addEventListener("error", reject);
            return;
        }
        const s = document.createElement("script");
        s.id = RECAPTCHA_SCRIPT_ID;
        s.src =
            "https://www.google.com/recaptcha/api.js?onload=__kcRecaptchaOnload&render=explicit";
        s.async = true;
        s.defer = true;
        s.onerror = reject;
        document.head.appendChild(s);
    });
    return window.__kcRecaptchaPromise;
}

export default function Login() {
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [stage, setStage] = useState("email"); // email | code
    const [devCode, setDevCode] = useState("");
    const [smtpError, setSmtpError] = useState(false);
    const [smtpEnabled, setSmtpEnabled] = useState(null); // null=unknown, true=email, false=dev/inactive
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const { config } = usePlatformConfig();
    const navigate = useNavigate();

    // reCAPTCHA — lazy-loaded only when configured. `widgetId` is needed to
    // call grecaptcha.reset() between failed attempts.
    const [recaptcha, setRecaptcha] = useState({
        enabled: false,
        site_key: "",
    });
    const [recaptchaToken, setRecaptchaToken] = useState("");
    const recaptchaRef = useRef(null);
    const widgetIdRef = useRef(null);

    // Fetch the public reCAPTCHA config on mount. When enabled, render the
    // checkbox into `recaptchaRef`. The widget calls back into our state so
    // we know the user passed the challenge before allowing the form submit.
    useEffect(() => {
        let alive = true;
        api.get("/auth/recaptcha-config")
            .then((r) => {
                if (!alive) return;
                setRecaptcha(r.data);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, []);

    // Fetch the public SMTP status on mount so the login copy reflects the
    // real delivery mode: when inactive (dev / SMTP off) we must NOT claim
    // an email was sent — the code is shown on screen instead.
    useEffect(() => {
        let alive = true;
        api.get("/auth/smtp-status")
            .then((r) => {
                if (!alive) return;
                setSmtpEnabled(r.data?.enabled === true);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        if (!recaptcha.enabled || !recaptcha.site_key) return;
        if (stage !== "email") return;
        let cancelled = false;
        loadRecaptchaScript()
            .then((g) => {
                if (cancelled || !g || !recaptchaRef.current) return;
                // If we re-rendered with the same node, reset instead of re-render.
                if (widgetIdRef.current !== null) {
                    try {
                        g.reset(widgetIdRef.current);
                    } catch {
                        /* ignore */
                    }
                    return;
                }
                widgetIdRef.current = g.render(recaptchaRef.current, {
                    sitekey: recaptcha.site_key,
                    callback: (token) => setRecaptchaToken(token),
                    "expired-callback": () => setRecaptchaToken(""),
                    "error-callback": () => setRecaptchaToken(""),
                });
            })
            .catch(() => {
                /* swallow — fall back to no captcha visible. Backend will reject. */
            });
        return () => {
            cancelled = true;
        };
    }, [recaptcha.enabled, recaptcha.site_key, stage]);

    const requestLink = async (e) => {
        e?.preventDefault();
        if (!email) return;
        if (recaptcha.enabled && !recaptchaToken) {
            toast.error("Completa el reCAPTCHA antes de continuar.");
            return;
        }
        setLoading(true);
        try {
            const r = await api.post("/auth/magic-link", {
                email,
                recaptcha_token: recaptchaToken || undefined,
            });
            // Backend returns `dev_code` in two cases:
            //   1. SMTP disabled (dev mode) → smtp=false, dev_code present.
            //   2. SMTP enabled but the send failed (e.g. AWS SES rejected
            //      our IAM identity) → smtp=true, smtp_error=true, dev_code
            //      present as fallback so the user isn't locked out.
            // In both cases we show the code under the input so login works.
            const isSmtp = r.data?.smtp === true;
            const smtpFail = r.data?.smtp_error === true;
            setDevCode(r.data?.dev_code || "");
            setSmtpError(smtpFail);
            setSmtpEnabled(isSmtp);
            setStage("code");
            if (smtpFail) {
                toast.error("Error enviando el correo", {
                    description:
                        r.data?.message ||
                        "Usa el código de respaldo de abajo para entrar.",
                });
            } else if (isSmtp) {
                toast.success("Código enviado", {
                    description: r.data?.message || `Revisa tu bandeja en ${email}`,
                });
            } else if (r.data?.dev_code) {
                toast.success("Código generado", {
                    description: "Visible abajo (modo dev)",
                });
            } else {
                // Dev/inactive mode + unregistered email: nothing is sent and
                // no code is shown. Don't imply an email went out.
                toast.success("Modo dev — SMTP inactivo", {
                    description: "Si el correo estuviera registrado, el código aparecería aquí.",
                });
            }
        } catch (err) {
            // Reset captcha on any failure so the next attempt has a fresh token
            if (recaptcha.enabled && window.grecaptcha && widgetIdRef.current !== null) {
                try {
                    window.grecaptcha.reset(widgetIdRef.current);
                } catch {
                    /* ignore */
                }
                setRecaptchaToken("");
            }
            toast.error(err?.response?.data?.detail || "Error generando código");
        } finally {
            setLoading(false);
        }
    };

    const verify = async (e) => {
        e?.preventDefault();
        setLoading(true);
        try {
            const r = await api.post("/auth/verify", { email, code });
            login(r.data.token, r.data.user);
            toast.success(`Bienvenido, ${r.data.user.name}`);
            navigate("/");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Código inválido");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] bg-ink-950 relative overflow-hidden">
            {/* Left: atmospheric side */}
            <div className="hidden lg:flex relative grain overflow-hidden">
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(800px 500px at 20% 10%, rgba(101,103,255,0.10), transparent 60%), radial-gradient(700px 500px at 80% 90%, rgba(101,103,255,0.08), transparent 60%), #ffffff",
                    }}
                />
                <div className="relative z-10 flex flex-col justify-between p-14 w-full">
                    <div className="flex items-center gap-3">
                        <img
                            src={config.logo_url || "/krea-logo.png"}
                            alt={config.platform_name || "Krea.AI"}
                            className="h-10 w-auto object-contain"
                            draggable={false}
                        />
                        <span className="font-mono text-xs tracking-[0.3em] text-fog-400">
                            {(config.platform_name || "Krea.AI").toUpperCase()} · STUDIO
                        </span>
                    </div>
                    <div className="space-y-8 max-w-xl">
                        <h1 className="font-display text-6xl xl:text-7xl leading-[1.02] text-fog-100">
                            Diseña con{" "}
                            <em className="text-periwinkle-500 not-italic">
                                memoria
                            </em>{" "}
                            de marca.
                        </h1>
                        <p className="text-fog-400 text-base leading-relaxed max-w-md">
                            Una plataforma para creativos y estudios: reúne tus
                            referencias, las de quien admiras, tu sistema de
                            marca — y genera piezas nuevas con IA, fieles a tu
                            lenguaje.
                        </p>
                        <div className="flex items-center gap-6 pt-6">
                            <Pill label="Magic Link" />
                            <Pill label="IA · Image Gen" />
                            <Pill label="Brand-aware" />
                        </div>
                    </div>
                    <div className="font-mono text-[10px] tracking-widest text-fog-500 uppercase">
                        v0.1 · Feb 2026
                    </div>
                </div>
            </div>

            {/* Right: form */}
            <div className="flex items-center justify-center p-6 sm:p-10 relative">
                <div
                    data-testid="login-card"
                    className="w-full max-w-md rounded-3xl glass border hairline border-ink-600 p-10 shadow-glow"
                >
                    <div className="mb-8">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 mb-3">
                            {stage === "email" ? "01 / ACCESO" : "02 / VERIFICAR"}
                        </p>
                        <h2 className="font-display text-4xl text-fog-100">
                            {stage === "email"
                                ? "Entra con Magic Link"
                                : "Ingresa tu código"}
                        </h2>
                        <p className="text-fog-400 text-sm mt-2">
                            {stage === "email"
                                ? smtpEnabled === false
                                    ? "Modo desarrollo — el código se mostrará en pantalla, no se envía correo."
                                    : "Te enviaremos un código de 6 dígitos a tu correo."
                                : devCode
                                  ? `Modo dev — código visible abajo`
                                  : smtpEnabled === false
                                    ? `Modo dev — SMTP inactivo. Si el correo estuviera registrado, el código aparecería aquí.`
                                    : `Si tu correo está registrado, en unos segundos verás el código en tu bandeja de entrada.`}
                        </p>
                    </div>

                    {stage === "email" ? (
                        <form onSubmit={requestLink} className="space-y-5">
                            <Field
                                label="Email"
                                data-testid="login-email-input"
                                type="email"
                                autoFocus
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tu@empresa.com"
                                required
                            />
                            {recaptcha.enabled && recaptcha.site_key && (
                                <div
                                    data-testid="login-recaptcha"
                                    ref={recaptchaRef}
                                    className="flex justify-center"
                                />
                            )}
                            <button
                                type="submit"
                                data-testid="login-request-link-btn"
                                disabled={
                                    loading ||
                                    (recaptcha.enabled && !recaptchaToken)
                                }
                                className="w-full rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 active:bg-periwinkle-700 text-ink-950 font-medium py-3 transition-all duration-200 disabled:opacity-50"
                            >
                                {loading ? "Enviando…" : "Enviar código"}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={verify} className="space-y-5">
                            <Field
                                label="Código (6 dígitos)"
                                data-testid="login-code-input"
                                type="text"
                                inputMode="numeric"
                                autoFocus
                                maxLength={6}
                                value={code}
                                onChange={(e) =>
                                    setCode(
                                        e.target.value.replace(/\D/g, "").slice(0, 6),
                                    )
                                }
                                placeholder="••••••"
                                required
                                className="text-center tracking-[0.8em] font-mono text-xl"
                            />
                            {devCode && (
                                <div
                                    data-testid="login-dev-code-banner"
                                    className={`rounded-xl border hairline px-4 py-3 flex items-center justify-between ${
                                        smtpError
                                            ? "border-amber-500/50 bg-amber-500/10"
                                            : "border-ink-600 bg-ink-800/50"
                                    }`}
                                >
                                    <span
                                        className={`font-mono text-[10px] tracking-widest uppercase ${
                                            smtpError ? "text-amber-400" : "text-fog-400"
                                        }`}
                                    >
                                        {smtpError ? "Código de respaldo" : "Código dev"}
                                    </span>
                                    <code
                                        data-testid="login-dev-code-value"
                                        className="font-mono text-periwinkle-500 text-lg tracking-widest"
                                    >
                                        {devCode}
                                    </code>
                                </div>
                            )}
                            <button
                                type="submit"
                                data-testid="login-verify-btn"
                                disabled={loading || code.length < 6}
                                className="w-full rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 active:bg-periwinkle-700 text-ink-950 font-medium py-3 transition-all duration-200 disabled:opacity-40"
                            >
                                {loading ? "Verificando…" : "Entrar"}
                            </button>
                            <button
                                type="button"
                                data-testid="login-back-btn"
                                onClick={() => {
                                    setStage("email");
                                    setCode("");
                                    setDevCode("");
                                    setSmtpError(false);
                                }}
                                className="w-full text-fog-400 text-xs hover:text-fog-100 transition-colors"
                            >
                                ← Cambiar email
                            </button>
                        </form>
                    )}

                    <p className="mt-10 pt-6 border-t hairline border-ink-600 text-xs text-fog-500 font-mono">
                        Al continuar aceptas que tus referencias se almacenen de
                        forma segura para entrenar tu estilo de marca.
                    </p>
                </div>
            </div>
        </div>
    );
}

function Pill({ label }) {
    return (
        <span className="inline-flex items-center gap-2 rounded-full border hairline border-ink-600 bg-ink-900/40 px-3 py-1 font-mono text-[10px] tracking-widest text-fog-300 uppercase">
            <span className="w-1 h-1 rounded-full bg-periwinkle-500" /> {label}
        </span>
    );
}

function Field({ label, className = "", ...props }) {
    return (
        <label className="block">
            <span className="font-mono text-[10px] tracking-[0.25em] text-fog-400 uppercase">
                {label}
            </span>
            <input
                {...props}
                className={`mt-2 w-full rounded-xl bg-ink-800/60 border hairline border-ink-600 focus:border-periwinkle-500 text-fog-100 px-4 py-3 outline-none transition-colors placeholder:text-fog-500 ${className}`}
            />
        </label>
    );
}
